// =============================================
// coverArt — extract embedded album art from an audio file (dependency-free)
// =============================================
// Parses the ID3v2 tag of an MP3 to pull out the APIC (attached picture) frame and
// return it as an image Blob. Best-effort: any unsupported format / missing art /
// parse hiccup returns null, and the caller falls back to a manual cover or the
// gradient placeholder. Covers ID3v2.2 (PIC) and v2.3/2.4 (APIC).
// =============================================

function synchsafe(a: number, b: number, c: number, d: number): number {
  return (a << 21) | (b << 14) | (c << 7) | d;
}

function latin1(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

interface Pic { mime: string; data: Uint8Array; }

function parsePicFrame(d: Uint8Array, major: number): Pic | null {
  let i = 0;
  const enc = d[i++];
  let mime: string;
  if (major === 2) {
    // v2.2 PIC: 3-char image format code (e.g. "JPG", "PNG")
    const fmt = String.fromCharCode(d[i], d[i + 1], d[i + 2]); i += 3;
    mime = fmt.toUpperCase() === 'PNG' ? 'image/png' : 'image/jpeg';
  } else {
    // v2.3/2.4 APIC: null-terminated MIME string
    const start = i;
    while (i < d.length && d[i] !== 0) i++;
    mime = latin1(d.subarray(start, i)) || 'image/jpeg';
    i++; // skip null terminator
  }
  i++; // picture-type byte
  // Skip the (encoding-dependent) description string.
  if (enc === 1 || enc === 2) { // UTF-16 → double-null terminator
    while (i + 1 < d.length && !(d[i] === 0 && d[i + 1] === 0)) i += 2;
    i += 2;
  } else { // latin1 / UTF-8 → single null
    while (i < d.length && d[i] !== 0) i++;
    i++;
  }
  if (i >= d.length) return null;
  return { mime, data: d.subarray(i) };
}

/** Extract embedded cover art from `file`, or null if none/unsupported. */
export async function extractCoverFromFile(file: File): Promise<Blob | null> {
  try {
    const head = new Uint8Array(await file.slice(0, 10).arrayBuffer());
    if (head.length < 10) return null;
    // "ID3" magic
    if (head[0] !== 0x49 || head[1] !== 0x44 || head[2] !== 0x33) return null;
    const major = head[3];
    const flags = head[5];
    const size = synchsafe(head[6], head[7], head[8], head[9]);
    const tag = new Uint8Array(await file.slice(10, 10 + size).arrayBuffer());

    let p = 0;
    // Skip extended header if present.
    if (flags & 0x40) {
      if (major === 4) p += synchsafe(tag[0], tag[1], tag[2], tag[3]);
      else { p += 4 + ((tag[0] << 24) | (tag[1] << 16) | (tag[2] << 8) | tag[3]); }
    }

    const idLen = major === 2 ? 3 : 4;
    const headerLen = major === 2 ? 6 : 10;
    while (p + headerLen <= tag.length) {
      const id = String.fromCharCode(...Array.from(tag.subarray(p, p + idLen)));
      if (id.charCodeAt(0) === 0) break; // padding
      let frameSize: number;
      if (major === 4) frameSize = synchsafe(tag[p + 4], tag[p + 5], tag[p + 6], tag[p + 7]);
      else if (major === 3) frameSize = (tag[p + 4] << 24) | (tag[p + 5] << 16) | (tag[p + 6] << 8) | tag[p + 7];
      else frameSize = (tag[p + 3] << 16) | (tag[p + 4] << 8) | tag[p + 5];
      if (frameSize <= 0) break;
      const dataStart = p + headerLen;
      const dataEnd = dataStart + frameSize;
      if (dataEnd > tag.length) break;
      const isPic = (major === 2 && id === 'PIC') || (major !== 2 && id === 'APIC');
      if (isPic) {
        const pic = parsePicFrame(tag.subarray(dataStart, dataEnd), major);
        if (pic && pic.data.length > 0) {
          // Copy into a fresh buffer so the Blob doesn't retain the whole tag array.
          return new Blob([pic.data.slice()], { type: pic.mime });
        }
      }
      p = dataEnd;
    }
    return null;
  } catch {
    return null;
  }
}

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
const MAX_COVER = 8 * 1024 * 1024; // 8 MB

/** Validate a user-picked cover image (manual set/replace). */
export function isValidCover(file: File): boolean {
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  return file.type.startsWith('image/') && IMAGE_EXTS.includes(ext) && file.size <= MAX_COVER;
}
